import { z } from "zod";
import { PropertyKindEnum } from "@/zod/properties.schema";

// --- Reusable Helper Schemas ---

const MediaUrlSchema = z.string().refine(
  (value) => {
    if (value.startsWith("/") && !value.startsWith("//")) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "Media must have a valid absolute or root-relative URL." },
);

// For simple { en, fa } text pairs (e.g., KeyFeature)
const LocalizedTextSchema = z.object({
  text_en: z.string().min(1, { message: "English text cannot be empty." }),
  text_fa: z.string().optional(),
});

// For items with both a title and text in two languages (e.g., MechChar)
const LocalizedTitledTextSchema = z.object({
  title_en: z.string().min(1, { message: "English title cannot be empty." }),
  title_fa: z.string().optional(),
  text_en: z.string().min(1, { message: "English text cannot be empty." }),
  text_fa: z.string().optional(),
});

// --- Schemas for Nested Models (API Input) ---

const KeyFeatureInputSchema = LocalizedTextSchema;
const StandardsInputSchema = LocalizedTextSchema;

const MechCharInputSchema = LocalizedTitledTextSchema;
const PhysCharInputSchema = LocalizedTitledTextSchema;
const FireStandardInputSchema = LocalizedTitledTextSchema;

const HighlightInfoInputSchema = LocalizedTextSchema;

const HighlightInputSchema = z.object({
  image_url: MediaUrlSchema,
  assetId: z.string().cuid().optional().nullable(),
  title_en: z.string().min(1),
  title_fa: z.string().optional(),
  info: z
    .array(HighlightInfoInputSchema)
    .min(1, { message: "Each highlight must have at least one info point." }),
});

const DocumentInputSchema = z.object({
  url: MediaUrlSchema,
  assetId: z.string().cuid().optional().nullable(),
  file_date: z.string().datetime({ message: "Invalid date format." }),
  file_type: z.string().min(1),
  file_size: z
    .number()
    .int()
    .positive({ message: "File size must be a positive integer." }),
  file_lang: z.string().min(1),
  file_title: z.string().min(1),
  file_slug: z.string().min(1),
});

// Permissive document schema for client-side editing
// Allows empty strings or missing values while the user fills fields.
const DocumentFormSchema = z.object({
  id: z.string().optional(),
  url: MediaUrlSchema.or(z.literal("")), // empty during editing
  assetId: z.string().cuid().optional().nullable(),
  file_date: z.string().datetime().or(z.literal("")).optional(),
  file_type: z.string().min(1).or(z.literal("")),
  file_size: z.number().int().nonnegative().or(z.literal("")).optional(),
  file_lang: z.string().min(1).or(z.literal("")).optional(),
  file_title: z.string().min(1).or(z.literal("")),
  file_slug: z.string().min(1),
});

const ThreeDInputSchema = z.object({
  url: MediaUrlSchema,
  assetId: z.string().cuid().optional().nullable(),
  title_en: z.string().optional().nullable(),
  title_fa: z.string().optional().nullable(),
  exposure: z.number().min(0).optional().nullable(),
  shadow_intensity: z.number().min(0).optional().nullable(),
});

const ThreeDFormSchema = ThreeDInputSchema.extend({
  uuid: z.string().uuid(),
  id: z.string().cuid().optional(),
  url: MediaUrlSchema.or(z.literal("")),
  exposure: ThreeDInputSchema.shape.exposure.or(z.literal("")).optional(),
  shadow_intensity: ThreeDInputSchema.shape.shadow_intensity
    .or(z.literal(""))
    .optional(),
});

const ProductPropertyAssignmentSchema = z.object({
  propertyId: z.string().min(1),
  optionId: z.string().min(1),
  groupId: z.string().min(1).optional().nullable(),
});

const ProductPropertyWithMetaSchema = ProductPropertyAssignmentSchema.extend({
  property: z.object({
    id: z.string().min(1),
    code: z.string(),
    label_en: z.string(),
    label_fa: z.string().optional().nullable(),
    kind: PropertyKindEnum,
  }),
  option: z.object({
    id: z.string().min(1),
    value_en: z.string(),
    value_fa: z.string().optional().nullable(),
    value_code: z.string(),
    hex: z.string().optional().nullable(),
  }),
});

// --- Main Product Schema for API ---
// This is what your API endpoint will validate for creating a new product.

export const ProductCreateInputSchema = z.object({
  // Direct fields
  id: z.string().cuid().optional(),
  part_number: z.string().min(1, { message: "Part number is required." }),
  slug: z
    .string()
    .min(1, { message: "Slug is required." })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
      message: "Slug must use lowercase letters, numbers, and dashes only.",
    }),
  hero_image: MediaUrlSchema,
  heroAssetId: z.string().cuid().optional().nullable(),
  description_title_en: z
    .string()
    .min(1, { message: "Description title is required." }),
  description_title_fa: z.string().optional(),
  description_text_en: z
    .string()
    .min(1, { message: "Description text is required." }),
  description_text_fa: z.string().optional(),
  description_factory: z.string().nullable(),
  about_en: z.string().optional(),
  about_fa: z.string().optional(),
  category_image: z.string().nullable(),
  categoryAssetId: z.string().cuid().optional().nullable(),
  producer: z.string().nullable(),
  // Relational fields for nested creation
  key_features: z.array(KeyFeatureInputSchema).optional(),
  mech_chars: z.array(MechCharInputSchema).optional(),
  standards: z.array(StandardsInputSchema).optional(),
  phys_chars: z.array(PhysCharInputSchema).optional(),
  fire_standards: z.array(FireStandardInputSchema).optional(),
  highlights: z.array(HighlightInputSchema).optional(),
  documents: z.array(DocumentInputSchema).optional(),
  threeD: z.array(ThreeDInputSchema).optional(),
  category_ids: z
    .array(
      z.string().cuid({ message: "Each category ID must be a valid CUID." }),
    )
    .optional(),
  productProperty: z.array(ProductPropertyAssignmentSchema).optional(),
});

export type ProductCreateInput = z.infer<typeof ProductCreateInputSchema>;

// --- Schema for Product Updates ---
// For update operations, almost all fields are optional.
// Zod's .partial() method is perfect for this.
export const ProductUpdateInputSchema = ProductCreateInputSchema.partial();
export type ProductUpdateInput = z.infer<typeof ProductUpdateInputSchema>;

// --- Schemas for Client-Side State Management ---
// These schemas include the temporary 'uuid' needed for React keys and UI logic.
// The API schema will strip these 'uuid' fields upon validation.

export const HighlightClientStateSchema = HighlightInputSchema.extend({
  uuid: z.string().uuid(),
});

export type HighlightClientState = z.infer<typeof HighlightClientStateSchema>;

export type DocumentInput = z.infer<typeof DocumentInputSchema>;
export type ThreeDInput = z.infer<typeof ThreeDInputSchema>;
export type ThreeDFormState = z.infer<typeof ThreeDFormSchema>;

// You can create a full client-side state schema if needed
export const ProductClientStateSchema = ProductCreateInputSchema.extend({
  hero_image: MediaUrlSchema.or(z.literal("")),
  heroAssetId: z.string().cuid().optional().nullable(),
  // Allow partially filled documents during editing
  documents: z.array(DocumentFormSchema).optional(),
  highlights: z.array(HighlightClientStateSchema).optional(),
  threeD: z.array(ThreeDFormSchema).optional(),
  categoryAssetId: z.string().cuid().optional().nullable(),
  productProperty: z
    .array(
      z.union([ProductPropertyWithMetaSchema, ProductPropertyAssignmentSchema]),
    )
    .default([])
    .optional(),
});

export type ProductPropertyAssignment = z.infer<
  typeof ProductPropertyAssignmentSchema
>;

export type ProductPropertyWithMeta = z.infer<
  typeof ProductPropertyWithMetaSchema
>;

export type ProductClientState = z.infer<typeof ProductClientStateSchema>;
